import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Github, ArrowUpRight  } from 'lucide-react';
import { Project } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

interface ProjectCardProps {
  project: Project;
  index: number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index }) => {
  const { t } = useTranslation();
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      },
    }),
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      className="bg-sky-950 bg-opacity-65 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
    >
      <div className="relative">
        {/* Image with overlay */}
        <div className="h-48 overflow-hidden">
          <img
            src={project.imageUrl}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        </div>
        
        {/* Featured badge */}
        {project.featured && (
          <div className="absolute top-4 right-4">
            <span className="bg-sky-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              {t('projectCard.featured')}
            </span>
          </div>
        )}
      </div>
      
      <div className="p-6">
        {/* Title and description */}
        <Link to={`/projects/${project.id}`}>
          <h3 className="text-xl font-semibold text-white mb-2 hover:underline transition-colors">
            {project.title}
          </h3>
        </Link>
        <p className="text-primary-300 mb-4 line-clamp-3">
          {project.description}
        </p>
        
        {/* Technologies */}
        <div className="mb-4 flex flex-wrap gap-2">
          {project.technologies.map((tech, i) => (
            <span
              key={i}
              className="text-xs font-medium bg-opacity-60 bg-sky-950 text-white px-2.5 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>
        
        {/* Links */}
        <div className="flex space-x-4 mt-4">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              // rel="noopener noreferrer"
              className="flex items-center text-sm font-medium text-teal-300 hover:text-teal-400 hover:underline transition-colors"
            >
              <ExternalLink size={16} className="mr-1" />
              {t('projectCard.liveDemo')}
            </a>
          )}



          {project.live && (
            <a
              href={project.live}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm font-medium text-lime-400 hover:text-lime-500 hover:underline transition-colors"
            >
              <ArrowUpRight size={16} className="mr-1" />
              {t('projectCard.liveVersion')}
            </a>
          )}
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm font-medium text-primary-400 hover:text-black hover:underline transition-colors"
            >
              <Github size={16} className="mr-1" />
              {t('projectCard.code')}
            </a>
          )}
          <Link
            to={`/projects/${project.id}`}
            className="ml-auto text-sm font-medium text-white hover:underline transition-colors"
          >
            {t('projectCard.viewDetails')+"..."}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;